import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { QrCode, Download } from 'lucide-react';
import { toast } from 'sonner';
import { QrScanner } from '../components/QrScanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Visitors = () => {
  const { user } = useAuth();
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    visitor_name: '',
    visitor_phone: '',
    visit_date: '',
    purpose: '',
    unit_id: user?.unit_number || ''
  });

  useEffect(() => {
    if (user?.role === 'resident' || user?.role === 'owner') {
      loadPasses();
    }
  }, [user]);

  const loadPasses = async () => {
    try {
      const { data } = await axios.get(`${API}/visitors/passes`, { withCredentials: true });
      setPasses(data);
    } catch (error) {
      toast.error('Failed to load visitor passes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/visitors/passes`, formData, { withCredentials: true });
      toast.success('Visitor pass generated successfully');
      setPasses([data, ...passes]);
      setShowForm(false);
      setFormData({ visitor_name: '', visitor_phone: '', visit_date: '', purpose: '', unit_id: user?.unit_number || '' });
    } catch (error) {
      toast.error('Failed to generate visitor pass');
    }
  };

  const [scanCode, setScanCode] = useState('');
  const [scanResult, setScanResult] = useState(null);

  const handleScan = async (codeOverride) => {
    const code = codeOverride ?? scanCode;
    if (!code) return;
    try {
      const { data } = await axios.post(`${API}/visitors/scan/${code}`, {}, { withCredentials: true });
      setScanResult(data);
      setScanCode(code);
      toast.success('Visitor pass scanned successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid QR code');
      setScanResult(null);
    }
  };

  if (user?.role === 'security') {
    return (
      <div className="space-y-6" data-testid="visitors-scan-page">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Scan Visitor Pass</h1>
          <p className="text-sm text-[#64748b]">Verify visitor entry with QR code</p>
        </div>

        <Card className="p-6 bg-white border border-[#e2e8f0]">
          <div className="space-y-6">
            {/* Camera scanner */}
            <QrScanner
              onScan={(text) => handleScan(text)}
              onError={(msg) => toast.error(msg)}
            />

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-[#e2e8f0]" />
              <span className="text-xs uppercase tracking-widest text-[#64748b]">or enter manually</span>
              <div className="h-px flex-1 bg-[#e2e8f0]" />
            </div>

            {/* Manual entry fallback */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Enter QR Code</Label>
                <Input
                  value={scanCode}
                  onChange={(e) => setScanCode(e.target.value)}
                  placeholder="Scan or enter QR code..."
                  data-testid="qr-code-input"
                />
              </div>
              <Button onClick={() => handleScan()} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="scan-button">
                <QrCode className="mr-2 h-4 w-4" />
                Verify Pass
              </Button>
            </div>
          </div>
        </Card>

        {scanResult && (
          <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="scan-result">
            <div className="space-y-3">
              <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>Visitor Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-1">Name</p>
                  <p className="text-sm">{scanResult.visitor_name}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-1">Phone</p>
                  <p className="text-sm">{scanResult.visitor_phone}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-1">Unit</p>
                  <p className="text-sm">{scanResult.unit_id}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-1">Host</p>
                  <p className="text-sm">{scanResult.host_name}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-1">Visit Date</p>
                  <p className="text-sm">{scanResult.visit_date}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-1">Purpose</p>
                  <p className="text-sm">{scanResult.purpose}</p>
                </div>
              </div>
              <div className="pt-4 border-t border-[#e2e8f0]">
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">VALID PASS</span>
              </div>
            </div>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="visitors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Visitor Passes</h1>
          <p className="text-sm text-[#64748b]">Generate QR codes for your visitors</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
          data-testid="generate-pass-button"
        >
          <QrCode className="mr-2 h-4 w-4" />
          Generate Pass
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 bg-white border border-[#e2e8f0]">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="visitor-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Visitor Name</Label>
                <Input
                  value={formData.visitor_name}
                  onChange={(e) => setFormData({ ...formData, visitor_name: e.target.value })}
                  placeholder="Full name"
                  required
                  data-testid="visitor-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Phone Number</Label>
                <Input
                  value={formData.visitor_phone}
                  onChange={(e) => setFormData({ ...formData, visitor_phone: e.target.value })}
                  placeholder="Contact number"
                  required
                  data-testid="visitor-phone-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Visit Date</Label>
                <Input
                  type="date"
                  value={formData.visit_date}
                  onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                  required
                  data-testid="visit-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Purpose</Label>
                <Input
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder="Reason for visit"
                  required
                  data-testid="visit-purpose-input"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="submit-pass-button">
                Generate Pass
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Passes List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto"></div>
          </div>
        ) : passes.length === 0 ? (
          <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
            <QrCode className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
            <p className="text-[#64748b]">No visitor passes generated yet</p>
          </Card>
        ) : (
          passes.map((pass, index) => (
            <Card key={index} className="p-6 bg-white border border-[#e2e8f0] hover:shadow-sm transition-shadow" data-testid={`pass-card-${index}`}>
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>{pass.visitor_name}</h3>
                  <div className="space-y-1 text-sm text-[#64748b] mb-3">
                    <p>Phone: {pass.visitor_phone}</p>
                    <p>Visit Date: {pass.visit_date}</p>
                    <p>Purpose: {pass.purpose}</p>
                    <p>Unit: {pass.unit_id}</p>
                  </div>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    pass.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {pass.status}
                  </span>
                </div>
                {pass.qr_image && (
                  <div className="flex-shrink-0">
                    <img src={pass.qr_image} alt="QR Code" className="w-32 h-32 border border-[#e2e8f0] rounded-md" />
                    <p className="text-xs text-center text-[#64748b] mt-2">Show at gate</p>
                  </div>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
